import { useCallback, useEffect, useState } from "react";
import { Plus, StickyNote } from "lucide-react";
import { useSettings } from "@/context/SettingsContext";
import { MedicalAlert } from "@/components/MedicalAlert";
import { Button } from "@/components/ui/button";
import { EventFilters, EMPTY_FILTERS } from "@/components/EventFilters";
import { EventFormDialog } from "@/components/EventFormDialog";
import { EventRow } from "@/components/EventRow";
import { useT } from "@/lib/i18n";
import { api } from "@/lib/api";

export const PageHeader = ({ title, sub, action, image, imagePos = "center" }) => {
  if (!image) {
    return (
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8 fade-up">
        <div>
          <span className="checker block w-10 h-2.5 mb-3" />
          <h1 data-testid="page-title" className="font-heading font-extrabold uppercase text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-none">{title}</h1>
          {sub && <p className="text-slate-400 mt-2 text-sm sm:text-base">{sub}</p>}
        </div>
        {action}
      </div>
    );
  }
  return (
    <div data-testid="page-hero" className="relative overflow-hidden rounded-sm border border-[#282D3D] mb-8 min-h-[190px] sm:min-h-[240px] flex items-end fade-up">
      <img src={image} alt="" style={{ objectPosition: imagePos }} className="absolute inset-0 w-full h-full object-cover" />
      <div className="absolute inset-0 bg-gradient-to-r from-[#090A0F] via-[#090A0F]/80 to-[#090A0F]/10" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#090A0F]/90 via-transparent to-transparent" />
      <div className="relative w-full p-5 sm:p-8 flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <span className="checker block w-10 h-2.5 mb-3" />
          <h1 data-testid="page-title" className="font-heading font-extrabold uppercase text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-none">{title}</h1>
          {sub && <p className="text-slate-300 mt-2 text-sm sm:text-base max-w-xl">{sub}</p>}
        </div>
        {action}
      </div>
    </div>
  );
};

const TeamNotes = () => {
  const { settings } = useSettings();
  const { t } = useT();
  if (!settings.team_notes?.trim()) return null;
  return (
    <div data-testid="team-notes-callout" className="mb-6 border border-[#FFB800]/40 bg-[#FFB800]/5 rounded-sm p-4 flex gap-3">
      <StickyNote className="w-5 h-5 text-[#FFB800] shrink-0 mt-0.5" />
      <div>
        <div className="font-heading uppercase font-bold text-[#FFB800]">{t("team_notes")}</div>
        <p className="text-sm text-slate-300 whitespace-pre-line mt-1">{settings.team_notes}</p>
      </div>
    </div>
  );
};

export default function Events() {
  const { t } = useT();
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [events, setEvents] = useState(null);
  const [tracks, setTracks] = useState([]);
  const [open, setOpen] = useState(false);

  const load = useCallback(async () => {
    const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
    const { data } = await api.get("/events", { params });
    setEvents(data);
  }, [filters]);

  const loadTracks = useCallback(async () => {
    const { data } = await api.get("/events/tracks");
    setTracks(data);
  }, []);

  useEffect(() => {
    const id = setTimeout(load, 250);
    return () => clearTimeout(id);
  }, [load]);

  useEffect(() => {
    loadTracks();
  }, [loadTracks]);

  return (
    <div>
      <PageHeader
        title={t("events_title")}
        sub={t("events_sub")}
        image="/images/race.jpg"
        imagePos="70% center"
        action={
          <Button data-testid="new-event-btn" onClick={() => setOpen(true)}
            className="rounded-sm bg-[#FF3B30] hover:bg-[#e0332a] font-heading uppercase tracking-wide text-lg h-11 px-5 transition-transform duration-150 active:scale-95">
            <Plus className="w-5 h-5 mr-1" /> {t("new_event")}
          </Button>
        }
      />
      <TeamNotes />
      <MedicalAlert />
      <EventFilters filters={filters} setFilters={setFilters} tracks={tracks} />
      <div data-testid="events-list" className="mt-6 space-y-2.5">
        {events && events.length === 0 && (
          <div data-testid="events-empty" className="border border-dashed border-[#282D3D] rounded-sm p-12 text-center text-slate-500 font-mono text-sm">
            {t("no_events")}
          </div>
        )}
        {events?.map((ev, i) => <EventRow key={ev.id} event={ev} index={i} />)}
      </div>
      <EventFormDialog open={open} onOpenChange={setOpen} tracks={tracks} onSaved={() => { load(); loadTracks(); }} />
    </div>
  );
}
